<!-- Claim_Detail.vue — Sidebar panel showing full detail for a single claim.
     Give it an MBI + claim date + provider NPI. It fetches the patient subgraph,
     finds the matching claim, and renders providers, facilities, diagnoses,
     procedures, drugs, revenue centers. Same layout as the claim drawer in
     Patient_Subgraph. -->
<template>
  <transition name="drawer">
    <div v-if="visible" class="claim-detail-overlay" @click.self="$emit('close')">
      <div class="claim-detail-panel">
        <!-- Header -->
        <div class="drawer-header">
          <div>
            <span v-if="claim" class="drawer-type-badge" :style="{ background: laneColor }">{{ claim.CLM_TYPE_LABEL || claim.CLM_TYPE_CD }}</span>
            <span v-if="claim" class="drawer-date">{{ claim.CLM_FROM_DT }}</span>
            <span v-else class="text-xs text-gray-500">Loading…</span>
          </div>
          <button class="drawer-close" @click="$emit('close')">✕</button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex items-center justify-center py-12">
          <span class="text-xs text-gray-400">Loading…</span>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="px-4 py-3 text-xs text-red-600 bg-red-50">{{ error }}</div>

        <!-- No match -->
        <div v-else-if="!claim" class="px-4 py-12 text-center text-xs text-gray-400">
          No matching claim found
        </div>

        <!-- Claim detail -->
        <div v-else class="drawer-body">
          <div class="drawer-section">
            <div class="drawer-row"><span class="drawer-label">Claim ID</span><span class="drawer-value mono sensitive" data-spoof-kind="identifier">{{ claim.CLM_ID }}</span></div>
            <div class="drawer-row"><span class="drawer-label">Paid</span><span class="drawer-value">${{ Number(claim.CLM_PMT_AMT || 0).toLocaleString() }}</span></div>
            <div v-if="claim.CLM_ALLOWED_AMT" class="drawer-row"><span class="drawer-label">Allowed</span><span class="drawer-value">${{ Number(claim.CLM_ALLOWED_AMT || 0).toLocaleString() }}</span></div>
            <div v-if="claim.DGNS_DRG_CD && claim.DGNS_DRG_CD !== '0000'" class="drawer-row">
              <span class="drawer-label">DRG</span><span class="drawer-value mono">{{ claim.DGNS_DRG_CD }}</span>
            </div>
          </div>

          <!-- Providers -->
          <div v-if="providers.length" class="drawer-section">
            <h4>Providers</h4>
            <div v-for="p in providers" :key="p.NPI + p.role" class="drawer-card compact">
              <span class="dx-code">{{ p.role }}</span>
              <span class="dx-desc sensitive">{{ p.PROVIDER_NAME }} — {{ p.SPECIALTY }}</span>
            </div>
          </div>

          <!-- Facilities -->
          <div v-if="orgs.length" class="drawer-section">
            <h4>Facilities</h4>
            <div v-for="o in orgs" :key="o.ORG_ID + o.role" class="drawer-card compact">
              <span class="dx-code">{{ o.role }}</span>
              <span class="dx-desc sensitive">{{ o.FACILITY_NAME }}</span>
            </div>
          </div>

          <!-- Diagnoses -->
          <div v-if="diagnoses.length" class="drawer-section">
            <h4>Diagnoses</h4>
            <div
              v-for="dx in diagnoses"
              :key="dx.CODE"
              class="drawer-card compact"
            >
              <span class="dx-code">{{ dx.CODE }}</span>
              <span class="dx-desc">{{ dx.SHORT_DESCRIPTION }}</span>
            </div>
          </div>

          <!-- Procedures (HCPCS) -->
          <div v-if="hcpcs.length" class="drawer-section">
            <h4>Procedures</h4>
            <div v-for="h in hcpcs" :key="h.CODE + (h.LINE_NUM || '')" class="drawer-card">
              <div class="card-title">{{ h.CODE }}
                <span v-if="h.ALLOWED_AMT && Number(h.ALLOWED_AMT) > 0" class="item-amt">${{ Number(h.ALLOWED_AMT).toLocaleString() }}</span>
              </div>
              <div class="card-subtitle" v-if="h.DESCRIPTION">{{ h.DESCRIPTION }}</div>
            </div>
          </div>

          <!-- Drugs (NDC) -->
          <div v-if="ndc.length" class="drawer-section">
            <h4>Drugs</h4>
            <div v-for="n in ndc" :key="n.CODE + (n.LINE_NUM || '')" class="drawer-card">
              <div class="card-title">{{ n.CODE }}
                <span v-if="n.ALLOWED_AMT && Number(n.ALLOWED_AMT) > 0" class="item-amt">${{ Number(n.ALLOWED_AMT).toLocaleString() }}</span>
              </div>
              <div class="card-subtitle" v-if="n.DESCRIPTION">{{ n.DESCRIPTION }}</div>
            </div>
          </div>

          <!-- Revenue Centers -->
          <div v-if="revCtr.length" class="drawer-section">
            <h4>Revenue Centers</h4>
            <div v-for="rc in revCtr" :key="rc.CODE + (rc.LINE_NUM || '')" class="drawer-card compact">
              <span class="dx-code">{{ rc.CODE }}</span>
              <span class="dx-desc">{{ rc.DESCRIPTION }}</span>
              <span v-if="rc.COVERED_AMT && Number(rc.COVERED_AMT) > 0" class="item-amt">${{ Number(rc.COVERED_AMT).toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'

const LANE_COLORS = {
  'INPATIENT': '#dc2626', 'OUTPATIENT': '#2563eb', 'PRIMARY CARE': '#059669',
  'HOME HEALTH': '#0891b2', 'HOSPICE': '#6b7280', 'DME': '#ca8a04',
  'PHARMACY': '#7c3aed', 'SNF': '#9333ea',
}

export default {
  name: 'Claim_Detail',
  props: {
    visible: { type: Boolean, default: false },
    mbi: { type: String, required: true },
    claimFromDt: { type: String, default: null },
    providerNpi: { type: String, default: null },

  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      nodes: {},
      edges: [],
    }
  },
  computed: {
    nodeIndex() {
      const idx = {}
      const keyFields = {
        Claim: 'CLM_ID', Practitioner: 'NPI', Organization: 'ORG_ID',
        Condition: 'CODE', Item: '_key',
        HCPCS_CODE: 'CODE', NDC_CODE: 'CODE', REV_CTR_CODE: 'CODE',
      }
      for (const [type, list] of Object.entries(this.nodes)) {
        const keyCol = keyFields[type]
        if (!keyCol) continue
        const map = {}
        for (const n of list) map[n[keyCol]] = n
        idx[type] = map
      }
      return idx
    },
    edgeByFrom() {
      const byFrom = {}
      for (const e of this.edges) {
        ;(byFrom[e.type] ||= {})[e.from] ||= []
        byFrom[e.type][e.from].push(e)
      }
      return byFrom
    },
    claim() {
      const claims = this.nodes.Claim || []
      for (const c of claims) {
        if (this.claimFromDt && c.CLM_FROM_DT !== this.claimFromDt) continue
        if (this.providerNpi) {
          const provEdges = (this.edgeByFrom['EOB_PRACTITIONER'] || {})[c.CLM_ID] || []
          if (!provEdges.some(e => e.to === this.providerNpi)) continue
        }
        return c
      }
      // If no filter matched, return first claim for this patient
      return claims[0] || null
    },
    laneColor() {
      if (!this.claim) return '#6b7280'
      return LANE_COLORS[this.claim.CLM_TYPE_LABEL] || '#6b7280'
    },
    providers() {
      if (!this.claim) return []
      return ((this.edgeByFrom['EOB_PRACTITIONER'] || {})[this.claim.CLM_ID] || [])
        .map(e => ({ ...(this.nodeIndex.Practitioner?.[e.to] || {}), role: e.ROLE || '' }))
        .filter(p => p.NPI)
    },
    orgs() {
      if (!this.claim) return []
      return ((this.edgeByFrom['EOB_ORGANIZATION'] || {})[this.claim.CLM_ID] || [])
        .map(e => ({ ...(this.nodeIndex.Organization?.[e.to] || {}), role: e.ROLE || '' }))
        .filter(o => o.ORG_ID)
    },
    diagnoses() {
      if (!this.claim) return []
      return ((this.edgeByFrom['EOB_CONDITION'] || {})[this.claim.CLM_ID] || [])
        .map(e => this.nodeIndex.Condition?.[e.to]).filter(Boolean)
    },
    items() {
      if (!this.claim) return []
      return ((this.edgeByFrom['EOB_ITEM'] || {})[this.claim.CLM_ID] || [])
        .map(e => this.nodeIndex.Item?.[e.to]).filter(Boolean)
    },
    hcpcs() {
      return this.items
        .filter(i => (i.CODE_TYPE || '').trim() === 'HCPCS' && (i.CODE || '').trim())
        .map(i => {
          const ce = (this.edgeByFrom['ITEM_HCPCS'] || {})[i._key] || []
          const cn = ce.length ? this.nodeIndex.HCPCS_CODE?.[ce[0].to] : null
          return cn ? { ...i, DESCRIPTION: cn.DESCRIPTION || '' } : i
        })
    },
    ndc() {
      return this.items
        .filter(i => (i.CODE_TYPE || '').trim() === 'NDC')
        .map(i => {
          const ce = (this.edgeByFrom['ITEM_NDC'] || {})[i._key] || []
          const cn = ce.length ? this.nodeIndex.NDC_CODE?.[ce[0].to] : null
          return cn ? { ...i, DESCRIPTION: cn.DESCRIPTION || '' } : i
        })
    },
    revCtr() {
      return this.items
        .filter(i => (i.CODE_TYPE || '').trim() === 'REV_CTR')
        .map(i => {
          const ce = (this.edgeByFrom['ITEM_REV_CTR'] || {})[i._key] || []
          const cn = ce.length ? this.nodeIndex.REV_CTR_CODE?.[ce[0].to] : null
          return cn ? { ...i, DESCRIPTION: cn.DESCRIPTION || '' } : i
        })
    },

  },
  watch: {
    visible(val) {
      if (val) this.fetchSubgraph()
    },
  },
  methods: {
    async fetchSubgraph() {
      if (!this.mbi) return
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(`/api/graph/patient/${this.mbi}/subgraph/`)
        this.nodes = data.nodes || {}
        this.edges = data.edges || []
      } catch (e) {
        this.error = e.response?.data?.error || e.message
      } finally {
        this.loading = false
      }
    },

  },
}
</script>

<style scoped>
.claim-detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
}
.claim-detail-panel {
  width: 360px;
  max-width: 90vw;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  height: 100%;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.drawer-type-badge {
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  margin-right: 6px;
}
.drawer-date { font-size: 12px; color: #6b7280; }
.drawer-close {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #9ca3af;
  padding: 0 4px;
}
.drawer-close:hover { color: #374151; }
.drawer-body { flex: 1; overflow-y: auto; }
.drawer-section {
  padding: 10px 14px;
  border-bottom: 1px solid #f3f4f6;
}
.drawer-section h4 {
  margin: 0 0 6px 0;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.drawer-row {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
  font-size: 12px;
}
.drawer-label { color: #6b7280; }
.drawer-value { color: #111827; font-weight: 500; }
.drawer-card {
  background: #f9fafb;
  border-radius: 5px;
  padding: 6px 8px;
  margin-bottom: 4px;
}
.drawer-card.compact {
  display: flex;
  gap: 6px;
  align-items: baseline;
  padding: 5px 8px;
}
.card-title {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  display: flex;
  justify-content: space-between;
}
.card-subtitle { font-size: 11px; color: #6b7280; margin-top: 1px; }
.item-amt { font-weight: 400; color: #059669; }
.dx-code {
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
  color: #7c3aed;
  white-space: nowrap;
}
.dx-desc { font-size: 11px; color: #374151; }
.mono { font-family: monospace; }

.drawer-enter-active, .drawer-leave-active { transition: opacity 0.2s ease; }
.drawer-enter-active .claim-detail-panel, .drawer-leave-active .claim-detail-panel { transition: transform 0.2s ease; }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-from .claim-detail-panel { transform: translateX(100%); }
.drawer-leave-to .claim-detail-panel { transform: translateX(100%); }
</style>
